import { useEffect } from 'react'

import { logError } from '@/utils/errorHandling'

/**
 * Global keyboard shortcuts for element detail pages.
 * 
 * Shortcuts:
 * - Ctrl/Cmd + S: Save
 * 
 * @example
 * ```tsx
 * useGlobalKeyboardShortcuts({
 *   enabled: editMode,
 *   onSave: handleSave,
 *   onError: (e) => onSaveError(e),
 * })
 * ```
 */
export interface UseGlobalKeyboardShortcutsConfig {
	/** Optional callback before save (e.g., to cancel autosave timer) */
	beforeSave?: () => void,
	/** Whether shortcuts are enabled */
	enabled: boolean,
	/** Error handler */
	onError?: (error: unknown) => void
	/** Save handler */
	onSave: () => Promise<void>
}

export const useGlobalKeyboardShortcuts = ({
	enabled,
	onSave,
	onError,
	beforeSave,
}: UseGlobalKeyboardShortcutsConfig) => {
	useEffect(() => {
		const handleKeyDown = async (event: KeyboardEvent) => {
			const { key, ctrlKey, metaKey } = event
			const modKey = ctrlKey || metaKey

			// Ctrl/Cmd + S: Save
			if (modKey && key === 's') {
				event.preventDefault()
				event.stopPropagation()

				if (enabled) {
					beforeSave?.()
					try {
						await onSave()
					} catch (e) {
						logError(e, { operation: 'useGlobalKeyboardShortcuts.handleKeyDown' })
						onError?.(e)
					}
				}
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [enabled, onSave, onError, beforeSave])
}

